import React, { useEffect, useRef, useState } from 'react';
import { gsap } from '../lib/gsap';
import { copy, Language } from '../content/i18n';

interface HeroProps {
  onWatchTrailer: () => void;
  language: Language;
}

const Hero: React.FC<HeroProps> = ({ onWatchTrailer, language }) => {
  const rootRef = useRef<HTMLElement>(null);
  const [ctaHovered, setCtaHovered] = useState(false);
  const t = copy[language].hero;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .fromTo('[data-h="nav"]',    { opacity: 0, y: -12 }, { opacity: 1, y: 0, duration: 0.8 }, 0.8)
        .fromTo('[data-h="logo"]',   { opacity: 0, scale: 1.06 }, { opacity: 1, scale: 1, duration: 1.4 }, 1.2)
        .fromTo('[data-h="over"]',   { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.8 }, 2.2)
        .fromTo('[data-h="title"]',  { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.0 }, 2.5)
        .fromTo('[data-h="rule"]',   { scaleX: 0 }, { scaleX: 1, duration: 0.8, ease: 'power2.inOut' }, 3.2)
        .fromTo('[data-h="sub"]',    { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.7 }, 3.2)
        .fromTo('[data-h="cta"]',    { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.7 }, 3.6)
        .fromTo('[data-h="scroll"]', { opacity: 0 },        { opacity: 1, duration: 0.6 },        4.2);
    }, rootRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={rootRef}
      id="hero"
      style={{
        position: 'relative',
        width: '100%',
        height: '100dvh',
        minHeight: 640,
        overflow: 'hidden',
        background: '#000',
      }}
    >
      {/* ─ Background video ─ */}
      <video
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center center',
          transform: 'scale(1.1)',
          transformOrigin: 'center center',
          filter: 'brightness(0.52) saturate(0.75)',
        }}
        src="/plan/geisha_hunter_2026_preview.mp4"
        autoPlay muted loop playsInline aria-hidden="true"
      />

      {/* ─ Vignette ─ */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at 50% 42%, transparent 28%, rgba(0,0,0,0.72) 80%)' }} />
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 20%, transparent 48%, rgba(0,0,0,1) 100%)' }} />

      {/* ─ Navigation ─ */}
      {/* <div
        data-h="nav"
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px 40px',
          opacity: 0,
        }}
      >
        <span style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.55rem',
          letterSpacing: '0.38em',
          color: 'rgba(255,255,255,0.38)',
          textTransform: 'uppercase',
        }}>
          A Documentary Film
        </span>
      </div> */}

      {/* ─ Centre content ─ */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 10,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '0 24px',
      }}>
        {/* Logo */}
        <div data-h="logo" style={{ opacity: 0, marginBottom: 28 }}>
          <img
            src="/plan/geisha-hunter-logo.png"
            alt="Geisha Hunter"
            style={{
              width: 'clamp(180px, 24vw, 320px)',
              height: 'auto',
              filter: 'drop-shadow(0 0 36px rgba(196,148,40,0.45))',
              display: 'block',
            }}
          />
        </div>

        {/* Overline */}
        <p data-h="over" style={{
          opacity: 0,
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.58rem',
          letterSpacing: '0.5em',
          color: '#c49428',
          textTransform: 'uppercase',
          fontWeight: 500,
          marginBottom: 18,
        }}>
          {t.overline}
        </p>

        {/* Main title */}
        <h1 data-h="title" style={{
          opacity: 0,
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(3.2rem, 9.5vw, 8.5rem)',
          fontWeight: 300,
          color: '#ffffff',
          letterSpacing: '0.22em',
          lineHeight: 1,
          textTransform: 'uppercase',
          textShadow: '0 0 80px rgba(0,0,0,0.6)',
          margin: 0,
        }}>
          {t.title}
        </h1>

        {/* Amber rule */}
        <div data-h="rule" style={{
          width: 56,
          height: 1,
          background: 'rgba(196,148,40,0.65)',
          margin: '22px auto',
          transformOrigin: 'center',
        }} />

        {/* Subtitle */}
        <p data-h="sub" style={{
          opacity: 0,
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.62rem',
          letterSpacing: '0.45em',
          color: 'rgba(255,255,255,0.5)',
          textTransform: 'uppercase',
          marginBottom: 40,
        }}>
          {t.subtitle}
        </p>

        {/* CTA */}
        <button
          data-h="cta"
          onClick={onWatchTrailer}
          aria-label={t.watchTrailerAria}
          onMouseEnter={() => setCtaHovered(true)}
          onMouseLeave={() => setCtaHovered(false)}
          style={{
            opacity: 0,
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.58rem',
            letterSpacing: '0.38em',
            fontWeight: 600,
            textTransform: 'uppercase',
            padding: '14px 42px',
            border: '1px solid rgba(196,148,40,0.65)',
            color: ctaHovered ? '#0a0a0a' : '#c49428',
            background: 'transparent',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            transition: 'color 0.32s ease',
          }}
        >
          <span
            style={{
              position: 'absolute', inset: 0,
              background: '#c49428',
              transform: ctaHovered ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.38s ease',
            }}
          />
          <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="8" height="10" viewBox="0 0 8 10" fill="currentColor" aria-hidden="true">
              <path d="M0 0l8 5-8 5z" />
            </svg>
            {t.watchTrailer}
          </span>
        </button>
      </div>

      {/* ─ Scroll cue ─ */}
      <div
        data-h="scroll"
        aria-hidden="true"
        style={{
          position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          opacity: 0,
        }}
      >
        <span style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '0.48rem',
          letterSpacing: '0.42em',
          color: 'rgba(255,255,255,0.22)',
          textTransform: 'uppercase',
        }}>{t.scroll}</span>
        <div
          className="animate-bounce"
          style={{ width: 1, height: 28, background: 'linear-gradient(to bottom, rgba(196,148,40,0.5), transparent)' }}
        />
      </div>
    </section>
  );
};

export default Hero;

